import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { Button } from './Button';
import { TextField } from './forms/TextField';
import { CheckboxField } from './forms/CheckboxField';
import { SweepLoader } from './SweepLoader';
import { isValidEmail, isValidPhone } from '../utils/validation';
type WaitlistFormProps = {
  tone?: 'light' | 'dark';
  source: string;
};

/** Watch waitlist. UI only — fields: email, mobile, sms_consent, source. */
export function WaitlistForm({
  tone = 'light',
  source
}: WaitlistFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dark = tone === 'dark';
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const email = String(data.get('email') ?? '');
    const mobile = String(data.get('mobile') ?? '').trim();
    const sms = data.get('sms_consent') === 'on';
    const next: Record<string, string> = {};
    if (!isValidEmail(email)) next.email = 'Enter a valid email address.';
    if (mobile && !isValidPhone(mobile)) next.mobile = 'Enter a valid mobile number.';
    if (sms && !mobile) next.mobile = 'Add your mobile number to receive texts.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  }
  if (status === 'success') {
    return <div role="status" className={`flex items-start gap-4 ${dark ? 'text-cream' : 'text-charcoal'}`} data-id="element-480" data-mp="1v7idzpo1yqsb">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-gold" data-id="element-481" data-mp="28x2cgjalp1vt">
          <CheckIcon aria-hidden="true" strokeWidth={1.25} className="h-4 w-4" data-id="element-482" data-mp="24027kbphm56a" />
        </span>
        <div data-id="element-483" data-mp="2ixcr88caahd3">
          <p className="t-h3 text-[1.25rem] text-inherit" data-id="element-484" data-mp="2ix30gijf066c">You're on the waitlist.</p>
          <p className={`mt-1 ${dark ? 'text-cream/80' : 'text-graphite'}`} data-id="element-485" data-mp="2dy9lelohc742">You'll be the first to know when the True Twelve Watch is ready.</p>
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate aria-label="True Twelve Watch waitlist" data-id="element-486" data-mp="2pyq27fl0a9ce">
      <input type="hidden" name="source" value={source} data-id="element-487" data-mp="2uy2n3usbh6cc" />
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-488" data-mp="2ux9lf887t712">
        <TextField tone={tone} label="Email" name="email" id={`${source}-email`} type="email" autoComplete="email" required error={errors.email} data-id="element-489" data-mp="2zpkoul25zayd" />
        <TextField tone={tone} label="Mobile" name="mobile" id={`${source}-mobile`} type="tel" autoComplete="tel" optional error={errors.mobile} data-id="element-490" data-mp="34o48getacank" />
      </div>
      <CheckboxField tone={tone} name="sms_consent" id={`${source}-sms`} className="mt-5" data-id="element-491" data-mp="3dosnncuf0h21">
        Text me when the watch launches. Msg &amp; data rates may apply; reply STOP to opt out.
      </CheckboxField>
      <Button type="submit" variant={dark ? 'light' : 'solid'} size="lg" disabled={status === 'submitting'} className="mt-7 w-full sm:w-auto" data-id="element-492" data-mp="1jc2tcu04x5u4">
        {status === 'submitting' ? <>
            <SweepLoader label="Joining waitlist" data-id="element-493" data-mp="1grocwvjb8c2w" /> Joining
          </> : 'Join the Waitlist'}
      </Button>
    </form>;
}